import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { DataCard, DataCardActions, DataCardEmpty, DataCardRow } from '../../components/ui/DataCard';
import { TextField } from '../../components/forms/TextField';
import { cn } from '../../utils/cn';
import { customersStore, type CustomerRecord } from './CustomersStore';
import { useI18n } from '../../i18n/I18nContext';

function Badge({ children, tone }: { children: React.ReactNode; tone: 'green' | 'rose' | 'gray' | 'red' }) {
  const tones: Record<typeof tone, string> = {
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
    gray: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', tones[tone])}>
      {children}
    </span>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{children}</div>
      <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
    </div>
  );
}

type CustomerDraft = {
  id?: string;
  name: string;
  email: string;
  username: string;
  password: string;
  phoneNo: string;
  address: string;
  country: string;
  dob: string;
  type: string;
};

const EMPTY_DRAFT: CustomerDraft = {
  name: '',
  email: '',
  username: '',
  password: '',
  phoneNo: '',
  address: '',
  country: 'ID',
  dob: '',
  type: '',
};

const DEBOUNCE_MS = 400;

export function CustomersPage() {
  const { t } = useI18n();
  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(10);
  const [skip, setSkip] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<CustomerDraft>({ ...EMPTY_DRAFT });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadCustomers = useCallback(async (params: { skip: number; limit: number; search: string }) => {
    setLoading(true);
    try {
      const result = await customersStore.fetch({
        skip: params.skip,
        limit: params.limit,
        search: params.search || undefined,
      });
      setCustomers(result.records);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('customers.loadFailed'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCustomers({ skip, limit, search: query });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSearchChange(value: string) {
    setQuery(value);
    setSkip(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadCustomers({ skip: 0, limit, search: value });
    }, DEBOUNCE_MS);
  }

  function handlePageChange(next: { skip: number; limit: number }) {
    setSkip(next.skip);
    setLimit(next.limit);
    loadCustomers({ skip: next.skip, limit: next.limit, search: query });
  }

  function refresh() {
    loadCustomers({ skip, limit, search: query });
  }

  function openCreate() {
    setError(null);
    setDraft({ ...EMPTY_DRAFT });
    setModalOpen(true);
  }

  function openEdit(customer: CustomerRecord) {
    setError(null);
    setDraft({
      id: customer.id,
      name: customer.name,
      email: customer.email,
      username: customer.username,
      password: '',
      phoneNo: customer.phoneNo,
      address: customer.address,
      country: customer.country,
      dob: customer.dob,
      type: customer.type,
    });
    setModalOpen(true);
  }

  async function saveDraft() {
    setError(null);
    const name = draft.name.trim();
    const email = draft.email.trim().toLowerCase();
    const username = draft.username.trim().toLowerCase() || email;
    if (!name) return setError(t('customers.validation.nameRequired'));
    if (!email || !email.includes('@')) return setError(t('customers.validation.emailInvalid'));

    setSaving(true);
    try {
      if (draft.id) {
        await customersStore.update(draft.id, {
          name,
          email,
          username,
          phoneNo: draft.phoneNo.trim(),
          address: draft.address.trim(),
          country: draft.country.trim(),
          dob: draft.dob || undefined,
          type: draft.type.trim() || undefined,
        });
      } else {
        if (!draft.password || draft.password.length < 6)
          return setError(t('customers.validation.passwordMinLength'));
        await customersStore.create({
          name,
          email,
          username,
          password: draft.password,
          phoneNo: draft.phoneNo.trim(),
          address: draft.address.trim(),
          country: draft.country.trim(),
          dob: draft.dob || undefined,
          type: draft.type.trim() || undefined,
        });
      }
      setModalOpen(false);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  function formatDob(dob: string) {
    if (!dob) return t('common.notAvailable');
    try {
      return new Date(dob).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dob;
    }
  }

  const statusTone = (s: string): 'green' | 'rose' => (s === 'active' ? 'green' : 'rose');
  const statusLabel = (status: string) => t(`common.status.${status.toLowerCase()}`) || status;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('customers.title')}</div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {t('customers.subtitle')}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
          <Button variant="secondary" onClick={refresh} disabled={loading}>
            {loading ? t('common.loading') : t('common.refresh')}
          </Button>
          <Button onClick={openCreate}>{t('customers.addCustomer')}</Button>
        </div>
      </div>

      {error && !modalOpen ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:max-w-sm">
            <TextField
              label={t('common.search')}
              placeholder={t('customers.searchPlaceholder')}
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {t('common.total')} <span className="font-semibold text-slate-700 dark:text-slate-300">{total}</span>
          </div>
        </div>

        {/* Mobile card list */}
        <div className="mt-4 space-y-3 md:hidden">
          {loading && customers.length === 0 ? (
            <DataCardEmpty>{t('customers.loadingCustomers')}</DataCardEmpty>
          ) : customers.length === 0 ? (
            <DataCardEmpty>{t('common.noResults')}</DataCardEmpty>
          ) : (
            customers.map((c) => (
              <DataCard key={c.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-900 dark:text-slate-100">{c.name}</div>
                    <div className="truncate text-xs text-slate-500 dark:text-slate-400">{c.email}</div>
                  </div>
                  <Badge tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge>
                </div>
                <div className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
                  <DataCardRow label={t('customers.table.phone')}>
                    {c.phoneNo || t('common.notAvailable')}
                  </DataCardRow>
                  <DataCardRow label={t('customers.table.dob')}>{formatDob(c.dob)}</DataCardRow>
                  {c.type ? (
                    <DataCardRow label={t('customers.table.name')}>{c.type}</DataCardRow>
                  ) : null}
                </div>
                <DataCardActions>
                  <Button variant="secondary" size="sm" onClick={() => openEdit(c)}>
                    {t('common.edit')}
                  </Button>
                </DataCardActions>
              </DataCard>
            ))
          )}
        </div>

        <div className="mt-4 hidden overflow-x-auto md:block">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-slate-500 dark:text-slate-400">
              <tr>
                <th className="py-2 pr-4 font-semibold">{t('customers.table.name')}</th>
                <th className="py-2 pr-4 font-semibold">{t('customers.table.email')}</th>
                <th className="py-2 pr-4 font-semibold">{t('customers.table.phone')}</th>
                <th className="py-2 pr-4 font-semibold">{t('customers.table.dob')}</th>
                <th className="py-2 pr-4 font-semibold">{t('customers.table.status')}</th>
                <th className="py-2 pr-0 font-semibold text-right">{t('customers.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && customers.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={6}>
                    {t('customers.loadingCustomers')}
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={6}>
                    {t('common.noResults')}
                  </td>
                </tr>
              ) : (
                customers.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-slate-900 dark:text-slate-100">{c.name}</div>
                      {c.type ? (
                        <div className="text-xs text-slate-500 dark:text-slate-400">{c.type}</div>
                      ) : c.createdAt ? (
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {t('customers.table.createdOn', { date: new Date(c.createdAt).toLocaleDateString() })}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">{c.email}</td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">{c.phoneNo || t('common.notAvailable')}</td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">{formatDob(c.dob)}</td>
                    <td className="py-3 pr-4">
                      <Badge tone={statusTone(c.status)}>{statusLabel(c.status)}</Badge>
                    </td>
                    <td className="py-3 pr-0">
                      <Button variant="secondary" size="sm" onClick={() => openEdit(c)}>
                        {t('common.edit')}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <Pagination
            total={total}
            limit={limit}
            skip={skip}
            onChange={handlePageChange}
          />
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={draft.id ? t('customers.editCustomer') : t('customers.addCustomer')}
        onClose={() => (saving ? null : setModalOpen(false))}
        size="md"
      >
        <div className="space-y-4">
          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          ) : null}

          <SectionLabel>{t('customers.sections.account')}</SectionLabel>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label={t('customers.fields.name')}
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder={t('customers.fields.namePlaceholder')}
            />
            <TextField
              label={t('customers.fields.email')}
              type="email"
              value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              placeholder={t('customers.fields.emailPlaceholder')}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label={t('customers.fields.username')}
              value={draft.username}
              onChange={(e) => setDraft((d) => ({ ...d, username: e.target.value }))}
              placeholder={t('customers.fields.usernamePlaceholder')}
            />
            {!draft.id ? (
              <TextField
                label={t('customers.fields.password')}
                type="password"
                value={draft.password}
                onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value }))}
                placeholder={t('customers.fields.passwordPlaceholder')}
              />
            ) : (
              <div />
            )}
          </div>

          <SectionLabel>{t('customers.sections.personalInfo')}</SectionLabel>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label={t('customers.fields.dob')}
              type="date"
              value={draft.dob}
              onChange={(e) => setDraft((d) => ({ ...d, dob: e.target.value }))}
            />
            <TextField
              label={t('customers.fields.phone')}
              type="tel"
              value={draft.phoneNo}
              onChange={(e) => setDraft((d) => ({ ...d, phoneNo: e.target.value }))}
              placeholder={t('customers.fields.phonePlaceholder')}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label={t('customers.fields.country')}
              value={draft.country}
              onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))}
              placeholder={t('customers.fields.countryPlaceholder')}
            />
            <TextField
              label={t('customers.fields.type')}
              value={draft.type}
              onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}
              placeholder={t('customers.fields.typePlaceholder')}
            />
          </div>

          <TextField
            label={t('customers.fields.address')}
            value={draft.address}
            onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
            placeholder={t('customers.fields.addressPlaceholder')}
          />

          <div className="flex justify-end gap-2 pt-2 [&>button]:flex-1 sm:[&>button]:flex-none">
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={saveDraft} disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
